import React, { useState } from "react";
import {
  FaFolder,
  FaFolderOpen,
  FaChevronRight,
  FaChevronDown,
  FaLock,
  FaLockOpen,
} from "react-icons/fa";

const FolderTree = ({
  folders,
  selectedFolder,
  setSelectedFolder,
  parentId = null,
  level = 0,
  onDeleteFolder,
  files = [],
  user,
  onToggleLock,
}) => {
  const [expanded, setExpanded] = useState({});

  let children = folders.filter(f => f.parentId === parentId);

  // Move selected folder to top
  if (selectedFolder) {
    const selectedIdx = children.findIndex(f => f.id === selectedFolder);
    if (selectedIdx > -1) {
      const selected = children[selectedIdx];
      children = [
        selected,
        ...children.filter((_, i) => i !== selectedIdx),
      ];
    }
  }

  const toggleExpand = id => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <ul
      style={{ paddingLeft: level * 16 }}
      className="transition-all duration-300"
    >
      {children.map(folder => {
        const isLocked = !!folder.locked;
        const isSuperAdmin = user?.role === "SUPERADMIN";
        const canOpen = !isLocked || isSuperAdmin;

        return (
          <li key={folder.id} className="mb-1">
            <div
              className={`flex items-center rounded-lg px-2 py-1 transition-colors duration-200 group
                ${canOpen ? "cursor-pointer" : "cursor-not-allowed"}
                ${
                  selectedFolder === folder.id
                    ? "bg-blue-100 shadow-md"
                    : canOpen
                    ? "hover:bg-gray-100"
                    : ""
                }`}
              onClick={() => {
                if (!canOpen) return;
                setSelectedFolder(
                  selectedFolder === folder.id ? null : folder.id
                );
              }}
            >
              {/* Expand / Collapse */}
              <span
                onClick={e => {
                  e.stopPropagation();
                  toggleExpand(folder.id);
                }}
                className="transition-transform duration-300 flex items-center"
                style={{
                  transform: expanded[folder.id]
                    ? "rotate(90deg)"
                    : "rotate(0deg)",
                }}
              >
                {expanded[folder.id] ? (
                  <FaChevronDown />
                ) : (
                  <FaChevronRight />
                )}
              </span>

              {/* Folder icon */}
              <span className="mx-2">
                {expanded[folder.id] ? (
                  <FaFolderOpen className="text-yellow-500 transition-transform duration-300 scale-110" />
                ) : (
                  <FaFolder className="text-yellow-500 transition-transform duration-300" />
                )}
              </span>

              {/* Lock icon */}
              <span className="mx-1">
                {isLocked ? (
                  <FaLock className="text-gray-500" title="Locked folder" />
                ) : (
                  <FaLockOpen
                    className="text-green-500"
                    title="Unlocked folder"
                  />
                )}
              </span>

              {/* Name & dates */}
              <div className="flex flex-1 items-center min-w-0">
                <span
                  className="font-medium group-hover:text-blue-700 transition-colors duration-200 truncate"
                  title={folder.name}
                >
                  {folder.name}
                </span>
                <span className="ml-3 text-gray-400 text-xs whitespace-nowrap">
                  <span className="block sm:hidden">
                    {formatDateOnly(
                      folder.updatedAt ||
                        folder.modifiedAt ||
                        folder.createdAt
                    )}
                  </span>
                  <span className="hidden sm:block">
                    {formatDate(
                      folder.updatedAt ||
                        folder.modifiedAt ||
                        folder.createdAt
                    )}
                  </span>
                </span>
              </div>

              {/* Lock / Unlock (SUPERADMIN) */}
              {isSuperAdmin && onToggleLock && (
                <button
                  className="ml-2 text-blue-500 hover:text-blue-700 px-2 py-1 rounded"
                  title={isLocked ? "Unlock folder" : "Lock folder"}
                  onClick={e => {
                    e.stopPropagation();
                    onToggleLock(folder);
                  }}
                >
                  {isLocked ? <FaLock /> : <FaLockOpen />}
                </button>
              )}

              {/* Delete */}
              {onDeleteFolder && (
                <button
                  className={`ml-auto text-red-500 hover:text-red-700 px-2 py-1 rounded transition-opacity
                    ${
                      selectedFolder === folder.id
                        ? "opacity-100"
                        : "opacity-60 hover:opacity-100"
                    }`}
                  title="Delete folder"
                  onClick={e => {
                    e.stopPropagation();
                    onDeleteFolder(folder);
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M6 6l12 12M6 18L18 6"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Children */}
            <div
              style={{
                maxHeight: expanded[folder.id] ? 500 : 0,
                overflow: "hidden",
                transition: "max-height 0.4s cubic-bezier(0.4,0,0.2,1)",
              }}
            >
              {expanded[folder.id] && (
                <>
                  <FolderTree
                    folders={folders}
                    selectedFolder={selectedFolder}
                    setSelectedFolder={setSelectedFolder}
                    parentId={folder.id}
                    level={level + 1}
                    onDeleteFolder={onDeleteFolder}
                    files={files}
                    user={user}
                    onToggleLock={onToggleLock}
                  />

                  {/* Files */}
                  {files
                    ?.filter(f => f.folderId === folder.id)
                    .map(file => (
                      <div
                        key={file.id}
                        className="flex items-center px-2 py-1 text-gray-700 bg-gray-50 rounded mb-1 text-xs"
                        style={{ marginLeft: (level + 1) * 16 }}
                      >
                        <span className="mr-2">📄</span>
                        <span className="truncate font-medium">
                          {file.name}
                        </span>
                        <span className="ml-2 text-gray-400">
                          {formatSize(file.size)}
                        </span>
                        <span className="ml-2 text-gray-400">
                          <span className="block sm:hidden">
                            {formatDateOnly(
                              file.updatedAt ||
                                file.modifiedAt ||
                                file.createdAt
                            )}
                          </span>
                          <span className="hidden sm:block">
                            {formatDate(
                              file.updatedAt ||
                                file.modifiedAt ||
                                file.createdAt
                            )}
                          </span>
                        </span>
                      </div>
                    ))}
                </>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

function formatSize(bytes) {
  if (bytes == null || isNaN(bytes)) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(2)} MB`;
  if (bytes < 1024 ** 4) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
  return `${(bytes / 1024 ** 4).toFixed(2)} TB`;
}

function formatDate(date) {
  if (!date) return "";
  let d = date;
  if (typeof d?.toDate === "function") d = d.toDate();
  else d = new Date(d);
  return isNaN(d.getTime()) ? "" : d.toLocaleString();
}

function formatDateOnly(date) {
  if (!date) return "";
  let d = date;
  if (typeof d?.toDate === "function") d = d.toDate();
  else d = new Date(d);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

export default FolderTree;